import React, { ReactNode } from 'react'
import { X } from 'phosphor-react'

type ModalSize = 'sm' | 'md' | 'lg'

interface ModalProps {
  title: string
  children: ReactNode
  isDanger?: boolean
  confirmButtonText?: string
  onCancel: () => void
  onConfirm: () => void
  onBack?: () => void
  backButtonText?: string
  secondaryAction?: {
    text: string
    onClick: () => void
  }
  isLoading?: boolean
  showCancel?: boolean
  size?: ModalSize
}

const sizeClasses: Record<ModalSize, string> = {
  sm: 'max-w-md',
  md: 'max-w-2xl',
  lg: 'max-w-4xl',
}

export const Modal: React.FC<ModalProps> = ({
  title,
  children,
  isDanger = false,
  confirmButtonText = 'Confirmer',
  onCancel,
  onConfirm,
  onBack,
  backButtonText = 'Précédent',
  secondaryAction,
  isLoading = false,
  showCancel = true,
  size = 'lg',
}) => {
  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4">
      <div
        className={`bg-white rounded-xl shadow-xl ${sizeClasses[size]} w-full max-h-[90vh] flex flex-col`}
      >
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-[var(--color-border)] px-6 py-4 flex justify-between items-center rounded-t-xl">
          <h2 className="text-2xl font-bold text-[var(--color-text-primary)]">{title}</h2>
          <button
            onClick={onCancel}
            className="text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)] transition-colors"
            aria-label="Fermer"
          >
            <X className="w-6 h-6" weight="regular" />
          </button>
        </div>

        {/* Body - Scrollable */}
        <div className="flex-1 overflow-y-auto px-6 py-6">{children}</div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-white border-t border-[var(--color-border)] px-6 py-4 flex justify-between items-center gap-3 rounded-b-xl">
          <div>
            {onBack && (
              <button
                type="button"
                onClick={onBack}
                disabled={isLoading}
                className="px-6 py-2 border border-[var(--color-border)] rounded-lg text-[var(--color-text-primary)] hover:bg-[var(--color-gray-100)] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {backButtonText}
              </button>
            )}
          </div>
          <div className="flex gap-3">
            {showCancel && (
              <button
                type="button"
                onClick={onCancel}
                disabled={isLoading}
                className="px-6 py-2 border border-[var(--color-border)] rounded-lg text-[var(--color-text-primary)] hover:bg-[var(--color-gray-100)] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Annuler
              </button>
            )}
            {secondaryAction && (
              <button
                type="button"
                onClick={secondaryAction.onClick}
                disabled={isLoading}
                className="px-6 py-2 border-2 border-[var(--color-primary)] rounded-lg text-[var(--color-primary)] hover:bg-[var(--color-primary)] hover:text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {secondaryAction.text}
              </button>
            )}
            <button
            type="button"
            onClick={onConfirm}
            disabled={isLoading}
            className={`px-6 py-2 rounded-lg text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
              isDanger
                ? 'bg-[var(--color-error)] hover:bg-[var(--color-error)]/90'
                : 'bg-[var(--color-primary)] hover:bg-[var(--color-primary)]/90'
            }`}
          >
            {isLoading ? 'Chargement...' : confirmButtonText}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
